'use client';

/**
 * DataTableToolbar Component
 *
 * Search and actions toolbar with glassmorphism styling.
 */

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import type React from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { transitionPresets } from '../../theme/transitions';
import type { DataTableToolbarProps } from '../../types/dataTable';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Icon Components
// ============================================================================

const SearchIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

const ClearIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const FilterListIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
  </svg>
);

const ViewColumnIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 18h5V5h-5v13zm-6 0h5V5H4v13zM16 5v13h5V5h-5z" />
  </svg>
);

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
}

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ theme, $isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  borderBottom: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  flexWrap: 'wrap',
}));

interface StyledTextFieldProps {
  $neonAccent?: NeonAccent;
  $isDark: boolean;
}

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledTextFieldProps>(({ theme, $neonAccent, $isDark }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    minWidth: 200,
    maxWidth: 320,

    '& .MuiOutlinedInput-root': {
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      borderRadius: 8,
      transition: transitionPresets.input,

      '& fieldset': {
        borderColor: $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      },

      '&:hover fieldset': {
        borderColor: neonStyles ? neonStyles.border : $isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
      },

      '&.Mui-focused fieldset': {
        borderColor: neonStyles ? neonStyles.base : theme.palette.primary.main,
        borderWidth: 1,
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
        boxShadow: neonStyles ? `0 0 10px ${neonStyles.glow}` : undefined,
      },
    },

    '& .MuiInputBase-input': {
      padding: theme.spacing(1, 1.5),
      fontSize: '0.875rem',

      '&::placeholder': {
        opacity: 0.5,
      },
    },
  };
});

interface StyledIconButtonProps {
  $neonAccent?: NeonAccent;
  $isDark: boolean;
  $active?: boolean;
}

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledIconButtonProps>(({ $neonAccent, $isDark, $active }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    color: $active && neonStyles ? neonStyles.base : $isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    backgroundColor: $active ? (neonStyles ? neonStyles.background : 'rgba(99, 91, 255, 0.1)') : 'transparent',
    transition: transitionPresets.allFast,

    '&:hover': {
      backgroundColor: neonStyles
        ? neonStyles.background
        : $isDark
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.04)',
      color: neonStyles ? neonStyles.base : undefined,
    },
  };
});

const ActionsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const LeftSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flex: 1,
}));

// ============================================================================
// Component
// ============================================================================

export const DataTableToolbar: React.FC<DataTableToolbarProps> = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  onFilterClick,
  onColumnVisibilityClick,
  actions,
  neonAccent,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();

  const handleClearSearch = () => {
    if (onSearchChange) {
      onSearchChange('');
    }
  };

  return (
    <StyledContainer $isDark={isDark}>
      <LeftSection>
        {onSearchChange && (
          <StyledTextField
            $neonAccent={neonAccent}
            $isDark={isDark}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ opacity: 0.5, display: 'flex', alignItems: 'center' }}>
                      <SearchIcon />
                    </Box>
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      sx={{
                        padding: 0.5,
                        color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        )}
        {actions}
      </LeftSection>

      <ActionsWrapper>
        {onFilterClick && (
          <GlassTooltip title={t('common.filter')}>
            <StyledIconButton $neonAccent={neonAccent} $isDark={isDark} onClick={onFilterClick} size="small">
              <FilterListIcon />
            </StyledIconButton>
          </GlassTooltip>
        )}
        {onColumnVisibilityClick && (
          <GlassTooltip title={t('common.columns')}>
            <StyledIconButton $neonAccent={neonAccent} $isDark={isDark} onClick={onColumnVisibilityClick} size="small">
              <ViewColumnIcon />
            </StyledIconButton>
          </GlassTooltip>
        )}
      </ActionsWrapper>
    </StyledContainer>
  );
};

export default DataTableToolbar;
